import { computed, inject, Signal } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';

import {
  GqlService,
  listProtocolDiscontinuationRatesQuery,
  RemoveProtocolDiscontinuationRateInput,
  SetProtocolDiscontinuationRateInput,
} from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { gqlToPromise } from '@shared/utils/gql-to-promise';
import { Maybe } from '@shared/utils/utils';

const protocolDiscontinuationRateKeys = {
  index: ['protocol-discontinuation-rate'] as const,
  list: (trialKey: string) =>
    [...protocolDiscontinuationRateKeys.index, 'list', { trialKey }] as const,
};

export const injectListProtocolDiscontinuationRates = () => {
  const gqlService = inject(GqlService);
  const mainQuery = inject(MainQuery);
  const flags = injectFlags({
    investigatorDiscontinuationRate: 'investigator_discontinuation_rate',
  });

  return injectQuery(() => ({
    enabled: flags().investigatorDiscontinuationRate,
    queryFn: () => {
      return gqlToPromise(() => gqlService.listProtocolDiscontinuationRates$());
    },
    queryKey: protocolDiscontinuationRateKeys.list(mainQuery.trialKey()),
  }));
};

export const refetchListProtocolDiscontinuationRates = () => {
  const queryClient = inject(QueryClient);
  const mainQuery = inject(MainQuery);
  const trialKey = mainQuery.trialKey();

  return queryClient.invalidateQueries({
    queryKey: protocolDiscontinuationRateKeys.list(trialKey),
  });
};

export const injectProtocolDiscontinuationRate = ({
  patientGroup,
  protocolVersionId,
}: {
  patientGroup: Signal<Maybe<string>>;
  protocolVersionId: Signal<Maybe<string>>;
}) => {
  const listProtocolDiscontinuationRates = injectListProtocolDiscontinuationRates();

  return computed(() => {
    return {
      data: listProtocolDiscontinuationRates
        .data()
        ?.find(
          (rate) =>
            rate.patient_group_id === patientGroup() &&
            rate.protocol_version_id === protocolVersionId()
        ),
      isLoading: listProtocolDiscontinuationRates.isLoading(),
    };
  });
};

export const injectSetProtocolDiscontinuationRate = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const mainQuery = inject(MainQuery);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (params: SetProtocolDiscontinuationRateInput) => {
      return gqlToPromise(() => gqlService.setProtocolDiscontinuationRate$(params));
    },
    onError: (error: Error) => {
      overlayService.error(error.message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        protocolDiscontinuationRateKeys.list(mainQuery.trialKey()),
        (oldData?: listProtocolDiscontinuationRatesQuery[]) => {
          const uniqOldData = oldData?.filter((item) => item.id !== data?.id) || [];

          return [...uniqOldData, data];
        }
      );
    },
  }));
};

export const injectRemoveProtocolDiscontinuationRate = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const mainQuery = inject(MainQuery);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (params: RemoveProtocolDiscontinuationRateInput) => {
      return gqlToPromise(() => gqlService.removeProtocolDiscontinuationRate$(params));
    },
    onError: (error: Error) => {
      overlayService.error(error.message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        protocolDiscontinuationRateKeys.list(mainQuery.trialKey()),
        (oldData?: listProtocolDiscontinuationRatesQuery[]) => {
          if (!oldData) return [];

          return oldData.filter((rate) => rate.id !== data?.id);
        }
      );
    },
  }));
};
